import React, { useEffect, useState } from "react";
import moment from "moment";
import { addOrderNote, getOrderNotes } from "../_redux/order/orderCrud";

const MAX_LENGTH = 2000;

/**
 * Order Notes: notes staff leave on an order (special instructions and the
 * like). Each shows who wrote it and when. Notes cannot be edited or deleted.
 *
 * @param {Object} props
 * @param {number|string} props.orderId The order the notes belong to.
 * @returns {JSX.Element|null}
 */
export function OrderNotesPanel({ orderId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Load the order's notes, newest first. State is only set once the request
  // settles, and not at all if the order changed or the panel went away.
  useEffect(() => {
    if (!orderId) return undefined;
    let current = true;

    getOrderNotes(orderId)
      .then((res) => {
        if (!current) return;
        setNotes(res?.data?.data || []);
        setError("");
      })
      .catch((err) => {
        if (!current) return;
        setError(
          err?.response?.data?.message || "The notes could not be loaded."
        );
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [orderId]);

  /**
   * Save the typed note, then show it at the top of the list.
   *
   * @param {React.FormEvent} event
   * @returns {void}
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    const note = text.trim();
    if (!note || saving) return;

    setSaving(true);
    addOrderNote(orderId, note)
      .then((res) => {
        const saved = res?.data?.data;
        if (saved) setNotes((current) => [saved, ...current]);
        setText("");
        setError("");
      })
      .catch((err) =>
        setError(
          err?.response?.data?.message || "The note could not be saved."
        )
      )
      .finally(() => setSaving(false));
  };

  if (!orderId) return null;

  return (
    <div className="card card-custom gutter-b">
      <div className="card-header align-items-center border-0 mt-4 mb-0">
        <h3 className="card-title align-items-start flex-column">
          <span className="font-weight-bolder text-dark">Order Notes</span>
        </h3>
      </div>
      <div className="card-body pt-0">
        <form onSubmit={handleSubmit}>
          <textarea
            className="form-control"
            rows={3}
            maxLength={MAX_LENGTH}
            placeholder="Add a note for anyone working on this order"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="New order note"
          />
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              {text.length}/{MAX_LENGTH}. Your name and the time are saved with
              the note.
            </small>
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              disabled={saving || !text.trim()}
            >
              {saving ? "Saving..." : "Add note"}
            </button>
          </div>
        </form>

        {error && <div className="text-danger mt-3">{error}</div>}

        <div className="mt-4">
          {loading && <div className="text-muted">Loading notes...</div>}
          {!loading && !error && notes.length === 0 && (
            <div className="text-muted">No notes on this order yet.</div>
          )}
          {notes.map((note) => (
            <div key={note.id} className="border-top py-3">
              <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {note.note}
              </div>
              <small className="text-muted">
                {note.author}
                {note.created_at
                  ? `, ${moment(note.created_at).format("DD-MM-YYYY HH:mm")}`
                  : ""}
              </small>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
